import type { FC, ReactNode } from 'react'
import type { HintKind } from '@/types'

import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'
import { ArrowLeft, Blocks } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDismiss } from '@/hooks/use-dismiss'
import { usePreferences } from '@/components/generals/providers'
import { Switch } from '@/components/ds/switch'
import { HINT_KIND_META } from '@/constants/confluence'

const LEGEND_DOTS: Record<HintKind, string> = {
    native: 'bg-success',
    addon: 'bg-warning',
    alternative: 'bg-subtle'
}

interface HintsPanelProps {
    onNavigate?: () => void
}

/** Design-review controls: toggles the Confluence implementation annotations on every page. */
export const HintsPanel: FC<HintsPanelProps> = ({ onNavigate }): ReactNode => {
    const { hints, setHints } = usePreferences()

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
                <p className='text-sm font-bold text-navy'>راهنمای پیاده‌سازی Confluence</p>
                <p className='text-small text-muted-foreground'>
                    قابلیت یا ماکروی معادل هر بخش، روی همان بخش نمایش داده می‌شود.
                </p>
            </div>

            <div className='flex items-center justify-between gap-3 rounded-lg bg-secondary/70 px-3 py-2.5'>
                <span className='text-sm font-semibold text-navy'>نمایش راهنما روی صفحات</span>
                <Switch checked={hints} onChange={setHints} label='نمایش راهنمای پیاده‌سازی روی صفحات' />
            </div>

            <ul className='flex flex-col gap-2'>
                {(Object.keys(HINT_KIND_META) as HintKind[]).map(kind => (
                    <li key={kind} className='flex items-start gap-2 text-small text-muted-foreground'>
                        <span className={cn('mt-2 size-2 shrink-0 rounded-full', LEGEND_DOTS[kind])} aria-hidden='true' />
                        <span>
                            <span className='font-semibold text-foreground'>{HINT_KIND_META[kind].title}: </span>
                            {HINT_KIND_META[kind].description}
                        </span>
                    </li>
                ))}
            </ul>

            <div className='flex flex-col gap-1 border-t border-border pt-3'>
                {[
                    { href: '/implementation', label: 'نقشه کامل پیاده‌سازی' },
                    { href: '/design-system', label: 'سیستم طراحی و حالت‌ها' }
                ].map(link => (
                    <Link
                        key={link.href}
                        href={link.href}
                        onClick={onNavigate}
                        className='group flex items-center justify-between rounded-md px-2 py-1.5 text-sm font-semibold text-primary-strong transition-colors hover:bg-primary-soft hover:text-primary-deep'
                    >
                        {link.label}
                        <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                    </Link>
                ))}
            </div>
        </div>
    )
}

export const HintsMenu: FC = (): ReactNode => {
    const { hints } = usePreferences()
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const close = useCallback(() => setOpen(false), [])

    useDismiss(ref, open, close)

    return (
        <div ref={ref} className='relative hidden md:block'>
            <button
                type='button'
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                aria-haspopup='dialog'
                aria-label='راهنمای پیاده‌سازی Confluence'
                title='راهنمای پیاده‌سازی Confluence'
                className={cn(
                    'relative flex h-10 cursor-pointer items-center gap-2 rounded-lg px-2.5 text-small font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                    hints ? 'bg-primary-soft text-primary-deep hover:bg-primary/15' : 'text-muted-foreground hover:bg-secondary hover:text-navy aria-expanded:bg-secondary aria-expanded:text-navy'
                )}
            >
                <Blocks className='size-5' aria-hidden='true' />
                <span className='hidden xl:inline'>راهنمای Confluence</span>
                {hints && <span className='absolute end-1.5 top-1.5 size-2 rounded-full bg-primary ring-2 ring-card xl:static xl:ring-0' aria-hidden='true' />}
            </button>

            {open && (
                <div
                    role='dialog'
                    aria-label='راهنمای پیاده‌سازی Confluence'
                    className='absolute end-0 top-full z-50 mt-2 w-80 rounded-xl border border-border bg-popover p-4 shadow-pop animate-in fade-in slide-in-from-top-1 duration-150'
                >
                    <HintsPanel onNavigate={close} />
                </div>
            )}
        </div>
    )
}
